import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from 'react'
import {
  api, ApiError,
  type CharacterizationState, type FeedNote, type NoteRevision, type NoteTag,
} from '../api.js'
import { NoteComposer } from './NoteComposer.js'
import { asWords, formatDate } from './Pill.js'
import { T } from '../tokens.js'

/**
 * The account's note feed — **the record** (F-17, R-27, R-30, spec §7.7).
 *
 * It replaces Plan 4's read-only pinned-note card, which existed because this
 * plan owns capture and half a composer built early is a thing this plan would
 * have had to undo first.
 *
 * ## This is a different question from the drill-down summary, and both answers stand
 *
 * `GET /api/accounts/:id` serves *one* note — the pinned one, or the most
 * recent — and `http/routes/accounts.ts:588` excludes a redacted row from that
 * query, where the comment says the exclusion "is not optional and not a
 * nicety". This feed reads `GET /api/accounts/:id/notes`, which serves a
 * redacted row **with its body withheld**, and draws it.
 *
 * Both are right because they answer different questions. The summary asks
 * "what is the one note to show beside the figures", and a body somebody
 * withheld must never be that. The feed *is* the record, and a record with
 * silent holes is not one: a row that vanished would leave a rep unable to tell
 * "nothing was written" from "something was written and taken back". Those are
 * the first two of the five redaction rules `packages/server/src/notes/search.ts`
 * tabulates; say which surface you are in before changing either.
 *
 * ## The caption is a promise, so an edit cannot be silent
 *
 * "Entries are permanent — a correction is a new entry" is the mock's line and
 * it is true of creation. Task 7 made editing keep its history in
 * `note_revisions`, so a screen that silently replaced the text under that
 * caption would be lying to its user — spec §7.7 asks for a history "the UI
 * makes honest rather than hides". An edited note therefore says it was edited
 * and offers the superseded versions.
 *
 * `GET /api/notes/:id/history` answers **403** for a redacted note, which is
 * the third redaction rule: the history of a withheld body is the withheld
 * body. So the control is not offered on a redacted row, *and* the 403 is
 * handled anyway — a note can be redacted between the render and the click, and
 * a thrown error with nothing on screen is a button that does nothing.
 *
 * ## What is deliberately not here
 *
 * No `kind` pill (`Note` / `Amendment` / `Re-score`), no `Attach to` chips and
 * no `System` activity rows, all of which the mock draws. `schema.sql`'s
 * `notes` table has no column for any of them and nothing in this plan writes
 * one, so each would be a control whose state could never be read back. The
 * heading is therefore `Notes` rather than the mock's
 * `Notes & account activity`: the activity half is not built, and a heading
 * that names it would be the only place in the product claiming it exists.
 *
 * The grey author colour the mock keys off `who === 'System'` survives as an
 * author-keyed style and costs nothing, but nothing writes a System author yet.
 *
 * ## Structure is in `data-*`, not in computed styles
 *
 * Every fact a test needs about a row — pinned, edited, redacted — is an
 * attribute. jsdom answers most computed style queries with `''`, and three
 * separate classes of test on this campaign went green against gutted code by
 * measuring the environment rather than the component.
 */

/** Verbatim from the mock, and a promise the rest of this component keeps. */
export const PERMANENCE_CAPTION = 'Entries are permanent — a correction is a new entry'

/**
 * Display rule 4, carried over from Plan 4's card unchanged. Grey body text and
 * nothing else: no dashed box, no icon, no "add the first note". An account
 * nobody has written about is the normal state of a targeting list, and the
 * composer above is a standing affordance rather than a comment on this
 * account's emptiness.
 */
export const NO_NOTES = 'No note has been written about this account.'

export const FEED_FAILED = 'The notes for this account could not be loaded.'

/** A 403 from the history route, in the terms it actually means. */
export const HISTORY_REDACTED = 'This note has been redacted, so its earlier versions are withheld too.'

export const HISTORY_FAILED = 'That history could not be loaded.'

/**
 * Display rule 4 again, and the hardest sentence in this component to get
 * right. 212 of the 313 accounts on this book are uncharacterized and always
 * will be; an uncharacterized account is normal, not incomplete. So this
 * prompt names an opportunity and never a deficiency, and the test asserts it
 * carries none of `missing`, `incomplete` or `required`.
 */
export const CHARACTERIZATION_PROMPT
  = 'No building type is recorded for this account. If this visit turns one up, '
  + 'recording it sharpens the next estimate.'

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

/** The mock's left-ruled detail block, reused for both of the blocks it has. */
const detailBlock: CSSProperties = {
  borderLeft: `3px solid ${T.color.chipOff}`,
  padding: '6px 0 6px 12px',
  marginTop: 9,
  fontSize: 13,
  lineHeight: 1.5,
  color: T.color.muted,
}

const quietButton: CSSProperties = {
  border: `1.5px solid ${T.color.border}`,
  background: '#fff',
  color: T.color.body,
  padding: '3px 9px',
  fontFamily: T.fontFamily,
  fontSize: 10,
  fontWeight: T.weight.bold,
  letterSpacing: '0.07em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  cursor: 'pointer',
}

const rowPill: CSSProperties = {
  border: `1.5px solid ${T.color.rule}`,
  color: T.color.muted,
  background: '#fff',
  padding: '3px 10px',
  borderRadius: T.radius.pill,
  fontSize: 9.5,
  fontWeight: T.weight.bold,
  letterSpacing: '0.07em',
  textTransform: T.micro.transform,
}

type Load =
  | { phase: 'loading' }
  | { phase: 'failed' }
  | { phase: 'ready'; notes: FeedNote[] }

type History =
  | { phase: 'loading' }
  | { phase: 'ready'; revisions: NoteRevision[] }
  | { phase: 'failed'; message: string }

/** The two tags spec §7.7 says are worth a characterization prompt. */
const CHARACTERIZING_TAGS: readonly NoteTag[] = ['curtailment_constraint', 'site_condition']

export interface NoteFeedProps {
  accountId: string
  /**
   * Display rule 3's state, when the caller knows it.
   *
   * Optional, and absent means "was not told" rather than any of the three
   * values. The characterization prompt fires only on `uncharacterized`, so a
   * feed rendered without this prop shows none — a prompt this component cannot
   * justify is one it does not raise. `undetermined` is excluded on purpose:
   * somebody already looked and could not tell, and asking them again is the
   * reproach display rule 4 forbids.
   */
  characterization?: CharacterizationState
}

export function NoteFeed({ accountId, characterization }: NoteFeedProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  const [openHistory, setOpenHistory] = useState<string | null>(null)
  const [histories, setHistories] = useState<Record<string, History>>({})
  /** The note whose save raised the prompt, or `null`. */
  const [prompted, setPrompted] = useState<string | null>(null)
  /**
   * Notes whose prompt has been dismissed. A set rather than clearing
   * `prompted`, because "dismissing it must not re-prompt for that note" is a
   * property and not a moment: any later render that recomputes the prompt from
   * the same note id has to reach the same answer.
   */
  const [dismissed, setDismissed] = useState<readonly string[]>([])
  const alive = useRef(true)
  /**
   * Which request the feed is waiting for. A save refetches, and so does a
   * change of account, so two answers can be in flight at once and the slower
   * one is not the answer to the question on screen — the same guard
   * `AccountDetail.tsx` puts on its as-of requests, for the same reason.
   */
  const era = useRef(0)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  /** The one place this component fetches the feed. */
  const refresh = useCallback((): void => {
    const mine = era.current + 1
    era.current = mine
    void api.notes(accountId)
      .then((notes) => {
        if (alive.current && era.current === mine) setLoad({ phase: 'ready', notes })
      })
      .catch(() => {
        if (alive.current && era.current === mine) setLoad({ phase: 'failed' })
      })
  }, [accountId])

  useEffect(() => {
    setLoad({ phase: 'loading' })
    // Everything below is about *this* account's notes. Carrying an open
    // history or a live prompt across would attach one account's record to
    // another account's name for as long as the fetch took.
    setOpenHistory(null)
    setHistories({})
    setPrompted(null)
    setDismissed([])
    refresh()
    // `refresh` is keyed on `accountId`, so this fires exactly when the account
    // changes — one dependency rather than two that could disagree.
  }, [refresh])

  const onSaved = useCallback(({ id, tag }: { id: string; tag: NoteTag | null }): void => {
    // The feed is refetched rather than having the new row spliced in locally:
    // the server decides the ordering (pinned first), stamps the author and the
    // time, and is the only thing that knows what it actually wrote.
    refresh()
    if (characterization === 'uncharacterized' && tag !== null && CHARACTERIZING_TAGS.includes(tag)) {
      setPrompted(id)
    }
  }, [refresh, characterization])

  const toggleHistory = useCallback((noteId: string): void => {
    if (openHistory === noteId) {
      setOpenHistory(null)
      return
    }
    setOpenHistory(noteId)
    setHistories((current) => ({ ...current, [noteId]: { phase: 'loading' } }))
    void api.noteHistory(noteId)
      .then((revisions) => {
        if (!alive.current) return
        setHistories((current) => ({ ...current, [noteId]: { phase: 'ready', revisions } }))
      })
      .catch((error: unknown) => {
        if (!alive.current) return
        const message = error instanceof ApiError && error.code === 'note_redacted'
          ? HISTORY_REDACTED
          : HISTORY_FAILED
        setHistories((current) => ({ ...current, [noteId]: { phase: 'failed', message } }))
      })
  }, [openHistory])

  const showPrompt = prompted !== null && !dismissed.includes(prompted)

  return (
    <section
      data-testid="notes"
      style={{
        borderTop: `1.5px solid ${T.color.rule}`,
        paddingTop: 20,
        marginTop: 24,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
        {/*
          `Notes`, not the mock's `Notes & account activity`. There is no
          activity feed and no column that could hold one.
        */}
        <h2 style={{ ...microLabel, margin: 0 }}>Notes</h2>
        <p data-testid="permanence-caption" style={{ margin: 0, fontSize: 12, color: T.color.micro }}>
          {PERMANENCE_CAPTION}
        </p>
      </div>

      <NoteComposer accountId={accountId} onSaved={onSaved} />

      {showPrompt
        ? (
          <div
            data-testid="characterization-prompt"
            style={{
              border: `1px solid ${T.color.hairline}`,
              background: T.tint.blue,
              padding: '10px 12px',
              marginTop: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 16,
            }}
          >
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: T.color.body }}>
              {CHARACTERIZATION_PROMPT}
            </p>
            <button
              type="button"
              data-testid="dismiss-characterization"
              onClick={() => {
                setDismissed((current) => (prompted === null ? current : [...current, prompted]))
                setPrompted(null)
              }}
              style={quietButton}
            >
              Dismiss
            </button>
          </div>
        )
        : null}

      <div data-testid="note-feed" data-feed-phase={load.phase} style={{ marginTop: 16 }}>
        {load.phase === 'loading'
          ? <p style={{ fontSize: 14.5, color: T.color.micro }}>Loading this account&rsquo;s notes&hellip;</p>
          : null}
        {load.phase === 'failed'
          ? <p style={{ fontSize: 14.5, lineHeight: 1.55, color: T.color.body }}>{FEED_FAILED}</p>
          : null}
        {load.phase === 'ready' && load.notes.length === 0
          ? <p data-testid="no-note" style={{ fontSize: 14.5, color: T.color.micro }}>{NO_NOTES}</p>
          : null}
        {load.phase === 'ready'
          ? load.notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              history={openHistory === note.id ? histories[note.id] : undefined}
              onHistory={() => { toggleHistory(note.id) }}
            />
          ))
          : null}
      </div>
    </section>
  )
}

function NoteRow(
  { note, history, onHistory }: {
    note: FeedNote
    history: History | undefined
    onHistory: () => void
  },
): JSX.Element {
  // Read into locals so the narrowing survives into the JSX: `note.redactedAt`
  // is `string | null` and a boolean derived from it does not narrow the field.
  const redactedAt = note.redactedAt
  const editedAt = note.editedAt
  return (
    <article
      data-testid={`note-${note.id}`}
      data-note-pinned={note.pinned ? 'true' : 'false'}
      data-note-redacted={redactedAt === null ? 'false' : 'true'}
      data-note-edited={editedAt === null ? 'false' : 'true'}
      style={{
        display: 'flex',
        gap: 16,
        padding: '14px 0',
        borderBottom: `1px solid ${T.color.hairline}`,
      }}
    >
      {/*
        The mock's fixed 126px gutter, kept because it is what makes a column of
        timestamps a column. `flex: none` is the half that matters: without it a
        long author name on one row shifts every date on the page.

        The date and not the time. `formatDate` is this package's only date
        formatter and it is the one the one-pager uses; a second one written
        here to add a clock time is the kind of duplicate that ends up
        disagreeing with the document a rep hands the customer.
      */}
      <div
        data-testid="note-when"
        style={{ width: 126, flex: 'none', fontSize: 13, color: T.color.micro, lineHeight: 1.45 }}
      >
        {formatDate(note.createdAt)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            data-testid="note-author"
            style={{ fontSize: 14.5, fontWeight: T.weight.bold, color: T.color.ink }}
          >
            {note.authorName}
          </span>
          {note.tag === null
            ? null
            // The human label, never the storage identifier. `asWords` is the
            // same rule the characterization label follows.
            : <span data-testid="note-tag" style={rowPill}>{asWords(note.tag)}</span>}
          {note.pinned
            ? <span data-testid="note-pinned" style={{ ...rowPill, color: T.color.navy }}>Pinned</span>
            : null}
        </div>

        {redactedAt !== null
          ? (
            <div data-testid="note-redaction" style={detailBlock}>
              <span style={{ ...microLabel, color: T.color.warnInk }}>Redacted</span>
              {/*
                The reason, on its own line and in the record's own words. A
                redaction with no stated reason is a deletion with extra steps,
                which is why the store requires one (R-30); a screen that then
                declined to show it would put the requirement back where it
                started.
              */}
              <p data-testid="redaction-reason" style={{ margin: '4px 0 0', color: T.color.body }}>
                {note.redactionReason}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                {`Withheld on ${formatDate(redactedAt)}. The row stays: `
                  + 'a record with silent holes is not a record.'}
              </p>
            </div>
          )
          : (
            <div
              data-testid="note-text"
              style={{
                fontSize: 14.5, lineHeight: 1.6, marginTop: 6, color: T.color.ink,
              }}
            >
              {note.body}
            </div>
          )}

        {editedAt !== null
          ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <span data-testid="note-edited" style={{ fontSize: 12.5, color: T.color.micro }}>
                {`Edited ${formatDate(editedAt)} · `
                  + `${note.revisionCount} earlier ${note.revisionCount === 1 ? 'version' : 'versions'}`}
              </span>
              {/*
                Not offered on a redacted row: the route answers 403 and the
                history of a withheld body is the withheld body. The click
                handler still copes with a 403, because a note can be redacted
                between this render and that click.
              */}
              {redactedAt !== null
                ? null
                : (
                  <button
                    type="button"
                    data-testid="note-history"
                    aria-expanded={history !== undefined}
                    onClick={onHistory}
                    style={quietButton}
                  >
                    History
                  </button>
                )}
            </div>
          )
          : null}

        {history === undefined
          ? null
          : (
            <div data-testid="note-history-panel" style={detailBlock}>
              {history.phase === 'loading' ? <p style={{ margin: 0 }}>Loading the history&hellip;</p> : null}
              {history.phase === 'failed' ? <p style={{ margin: 0 }}>{history.message}</p> : null}
              {history.phase === 'ready'
                ? (
                  <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {history.revisions.map((revision) => (
                      <li key={revision.version} style={{ marginBottom: 8 }}>
                        <div style={{ ...microLabel, fontSize: 9.5 }}>
                          {`Version ${revision.version} · ${formatDate(revision.editedAt)}`}
                        </div>
                        <div style={{ color: T.color.body, marginTop: 3 }}>{revision.body}</div>
                      </li>
                    ))}
                  </ol>
                )
                : null}
            </div>
          )}
      </div>
    </article>
  )
}
